/**
 * What a refused Go actually was (GDN-3895).
 *
 * The launch handler used to catch its failure without binding it, so every
 * outcome — a refusal the server explained, a 500, a dropped connection —
 * became the same sentence, and the same catch marked conversion status
 * unknown for the WHOLE table. One row's refusal therefore blanked every row's
 * stage and disabled Run conversion and Add files across the page.
 *
 * Reading the error is the first half of separating that. Three facts were
 * wearing one boolean: whether the conversion service is reachable (the
 * table's), whether THIS row's launch failed, and whether THIS row is working.
 * This module answers the second, per row.
 *
 * ## Why this reads `failureClass` and not the status code
 *
 * `auth0BaseQuery` normalises every failure before RTK Query sees it. A dropped
 * connection, a timeout and a parse error all arrive as numeric **500**
 * (`auth0BaseQuery.ts:113`), so "the status is a number" does NOT mean the
 * server answered. Classifying on that would tell an operator their conversion
 * did not start when it may well be running — the exact false claim this change
 * exists to prevent.
 *
 * The same normaliser records what actually happened as `failureClass`, and
 * wraps its own envelope around the server's body: the rejection is
 * `{ status, data: { status, data: <server body>, message, failureClass } }`
 * (`auth0BaseQuery.ts:485`). So the server's JSON is two levels down, which is
 * why a single unwrap finds no code.
 *
 * Both are established here rather than deduced: `jot-support/submissionRejection.ts`
 * and `payer-connections/progressive.ts` read the same metadata the same way,
 * and admin-v2 already consumes the latter.
 */

/** As `auth0BaseQuery` classifies it. */
export type LaunchFailureClass = 'http' | 'timeout' | 'transport';

export interface LaunchRefusal {
  /** What kind of failure this was, from the base query's own metadata. */
  failureClass: LaunchFailureClass;
  /** The HTTP status. Meaningful only when `failureClass` is 'http'. */
  status?: number;
  /** The server's machine-readable code, when it sent one. */
  code?: string;
  /** The server's own sentence, when it sent one. */
  message?: string;
  /**
   * The request never got an answer, so the conversion may have started. A row
   * must not claim it did not. Anything other than an `http` failure is
   * unconfirmed, whatever status the normaliser put on it.
   */
  unconfirmed: boolean;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : undefined;
}

function readString(source: unknown, key: string): string | undefined {
  const value = asRecord(source)?.[key];
  return typeof value === 'string' ? value : undefined;
}

/** The code, wherever this API puts it: at the top level of the body or under `detail`. */
function readCode(body: unknown): string | undefined {
  return readString(body, 'code') ?? readString(asRecord(body)?.detail, 'code');
}

/**
 * Classify a rejected launch. Never throws: it runs on the failure path, and a
 * parser that could fail there would replace a refusal the operator can act on
 * with a blank screen.
 */
export function readLaunchRefusal(error: unknown): LaunchRefusal {
  const outer = asRecord(error);
  if (!outer) return { failureClass: 'transport', unconfirmed: true };

  // The normaliser's envelope, when it is there. A legacy or hand-made error
  // may not have one, so every read below falls back to the outer object.
  const normalised = asRecord(outer.data);
  const classified = normalised?.failureClass ?? outer.failureClass;
  const failureClass: LaunchFailureClass =
    classified === 'http' || classified === 'timeout' || classified === 'transport'
      ? classified
      : outer.status === 'TIMEOUT_ERROR'
        ? 'timeout'
        : typeof outer.status === 'number'
          ? 'http'
          : 'transport';

  const status =
    typeof normalised?.status === 'number'
      ? normalised.status
      : typeof outer.status === 'number'
        ? outer.status
        : undefined;

  // The server's own body sits inside the envelope; without one, the outer
  // `data` IS the body.
  const body = normalised ? (normalised.data ?? normalised) : outer.data;

  return {
    failureClass,
    status,
    code: readCode(body),
    message: readString(normalised, 'message') ?? readString(body, 'message'),
    unconfirmed: failureClass !== 'http',
  };
}

/**
 * What the row should say. Always a sentence: a refusal with nothing to show
 * would read as the row having no opinion.
 */
export function launchRefusalMessage(refusal: LaunchRefusal): string {
  if (refusal.unconfirmed) {
    return refusal.failureClass === 'timeout'
      ? 'The conversion request timed out. It may still have started — refresh before trying again.'
      : 'The conversion request could not be delivered. It may still have started — refresh before trying again.';
  }
  // The server explained itself; prefer its sentence over a generic one. The
  // normaliser has already replaced an empty body with a status-appropriate
  // default, so this is never blank.
  return refusal.message ?? 'The conversion request was refused. Refresh to check this source.';
}
